/**
 * "Filtrar por campo" (#568): a lista de Clientes e o funil recortam pelos
 * campos personalizados de lista, sim/não e data.
 *
 * Escolhe-se o campo, depois o valor no controle do tipo, e "Filtrar". Cada
 * filtro ativo vira uma pílula com o "x" que o tira. O estado é o texto do
 * filtro, o mesmo que vai na URL da API: quem usa só guarda a string.
 */

import { useState, type ReactNode } from 'react';
import type { CampoPersonalizado } from '@cliqcrm/contracts';
import { Botao, Campo, CampoSelecao, Icone } from '@/shared/ui';
import { comCondicao, filtravel, lerFiltro, rotuloDaCondicao, semCondicao } from '../lib/filtro';
import './campos-personalizados.css';

export function FiltroDeCampos({
  campos,
  filtro,
  aoMudar,
  id,
}: {
  campos: readonly CampoPersonalizado[];
  /** O texto do filtro, `''` sem filtro. */
  filtro: string;
  aoMudar: (filtro: string) => void;
  /** Prefixo dos ids dos controles, único na tela. */
  id: string;
}): ReactNode {
  const opcoes = campos.filter(filtravel).sort((a, b) => a.ordem - b.ordem);
  const [escolhido, definirEscolhido] = useState('');
  const [valor, definirValor] = useState('');
  const [ate, definirAte] = useState('');
  const ativos = lerFiltro(filtro);
  const campo = opcoes.find((item) => item.id === escolhido);

  if (opcoes.length === 0 && ativos.length === 0) return null;

  const valorPronto =
    campo?.tipo === 'data' ? valor !== '' || ate !== '' : campo !== undefined && valor !== '';

  const aplicar = () => {
    if (!campo || !valorPronto) return;
    aoMudar(
      comCondicao(filtro, {
        campoId: campo.id,
        valor: campo.tipo === 'data' ? `${valor}..${ate}` : valor,
      }),
    );
    definirEscolhido('');
    definirValor('');
    definirAte('');
  };

  return (
    <div className="campos-filtro" role="group" aria-label="Filtro por campo personalizado">
      {opcoes.length > 0 && (
        <div className="campos-filtro__escolha">
          <CampoSelecao
            id={`${id}-campo`}
            rotulo="Filtrar por campo"
            tamanho="miudo"
            value={escolhido}
            onChange={(evento) => {
              definirEscolhido(evento.currentTarget.value);
              definirValor('');
              definirAte('');
            }}
          >
            <option value="">Escolha um campo</option>
            {opcoes.map((item) => (
              <option key={item.id} value={item.id}>
                {item.nome}
              </option>
            ))}
          </CampoSelecao>

          {campo && (campo.tipo === 'lista_unica' || campo.tipo === 'lista_multipla') && (
            <CampoSelecao
              id={`${id}-valor`}
              rotulo={campo.nome}
              tamanho="miudo"
              value={valor}
              onChange={(evento) => definirValor(evento.currentTarget.value)}
            >
              <option value="">Escolha</option>
              {campo.opcoes
                .filter((opcao) => !opcao.arquivada)
                .map((opcao) => (
                  <option key={opcao.id} value={opcao.id}>
                    {opcao.nome}
                  </option>
                ))}
            </CampoSelecao>
          )}
          {campo?.tipo === 'sim_nao' && (
            <CampoSelecao
              id={`${id}-valor`}
              rotulo={campo.nome}
              tamanho="miudo"
              value={valor}
              onChange={(evento) => definirValor(evento.currentTarget.value)}
            >
              <option value="">Escolha</option>
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </CampoSelecao>
          )}
          {campo?.tipo === 'data' && (
            <>
              <Campo
                id={`${id}-de`}
                rotulo="De"
                type="date"
                tamanho="miudo"
                value={valor}
                onChange={(evento) => definirValor(evento.currentTarget.value)}
              />
              <Campo
                id={`${id}-ate`}
                rotulo="Até"
                type="date"
                tamanho="miudo"
                value={ate}
                onChange={(evento) => definirAte(evento.currentTarget.value)}
              />
            </>
          )}
          {campo && (
            <Botao
              miudo
              variante="secundario"
              desabilitado={!valorPronto}
              motivo="Escolha o valor."
              onClick={aplicar}
            >
              Filtrar
            </Botao>
          )}
        </div>
      )}

      {ativos.length > 0 && (
        <ul className="campos-filtro__ativos" aria-label="Filtros por campo ativos">
          {ativos.map((ativo) => {
            const rotulo = rotuloDaCondicao(
              campos.find((item) => item.id === ativo.campoId),
              ativo.valor,
            );
            return (
              <li key={ativo.campoId} className="campos-filtro__ativo">
                <span>{rotulo}</span>
                <button
                  type="button"
                  className="campos-filtro__tirar"
                  aria-label={`Tirar o filtro ${rotulo}`}
                  onClick={() => aoMudar(semCondicao(filtro, ativo.campoId))}
                >
                  <Icone nome="fechar" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
